<script setup>

import SubmitButtonLargeComponent from '@buttons/SubmitButtonLargeComponent.vue';
import ButtonComponent from '@buttons/ButtonComponent.vue';
import ProgressBarComponent from '@formFields/ProgressBarComponent.vue';
import {inject, ref, watch} from 'vue';
import {showErrorToast, showSuccessToast} from '@utils/Toast';
import {debounce} from 'lodash';
import useFileHandler from '@utils/useFileHandler';
import useFileDeleteHandler from '@utils/useFileDeleteHandler';
import ThumbnailComponentDelete from '@images/ThumbnailComponentDelete.vue';
import NoUploadedProfilePhotoFoundComponent from '@utils/NoUploadedProfilePhotoFoundComponent.vue';
import MainProgressBarComponent from '../../Helper/MainProgressBarComponent.vue';
import {handleErrorResponse} from '@utils/errorHandler';

const props = defineProps({
    csrf: String,
    routeUpload: String,
    route: String,
    routeSuccess: String,
    user: Object,
});

// Inject Axios
const axios = inject('axios');

const title = props.user.first_name +
    ', to help personalise your account please upload<br />a profile photo (optional).';

const LABEL_UPLOAD_PHOTO = 'Supported extensions are: JPG, JPEG, GIF, and PNG';

const isFormValid = ref(false);

const thumbnailSource = ref(props.user.thumbnailSource || null);
const fileName = ref(props.user.image || null);
const uploadPercentage = ref(0);

const delayedRedirection = debounce(() => {
    window.location.href = props.routeSuccess;
}, 2000);

const onUploadSuccess = (data) => {
    thumbnailSource.value = data.src;
    fileName.value = data.filename;
};

const onUploadProgress = (progress) => {
    uploadPercentage.value = progress;
};

function onDeleteSuccess() {
    thumbnailSource.value = null;
    fileName.value = null;
    uploadPercentage.value = 0;
}

const { handleFileUpload } = useFileHandler(props, onUploadSuccess, onUploadProgress);

const { deleteFile } = useFileDeleteHandler(props, onDeleteSuccess);

const validateForm = () => {
    // Check the validity of the input field
    isFormValid.value = fileName.value !== null;
};

const submitForm = async () => {
    validateForm();
    if(!isFormValid.value) {
        // showErrorToast('Invalid form values');
        return false;
    }
        //Prepare the data to be sent
        const formData = {
            image: fileName.value,
        };

        try {
            //Send a POST request to the server
            const response = await axios.post(props.route, formData, {
                headers: {
                    'X-CSRF-TOKEN': props.csrf // Include CSRF token here
                }
            });
            //Handle response here
            if(response.status === 200) {
                const data = response.data;
                if(data.success === 1) {
                    showSuccessToast(data.message);
                    delayedRedirection();
                } else {
                    showErrorToast(data.message);
                }
            } else {
                if(response.message) {
                    showErrorToast(response.message);
                }
            }

        } catch (error) {
            showErrorToast(handleErrorResponse(error));
            console.error('Submission error', error);
        }
};

</script>

<template>
    <div class="container sm:max-w-6xl text-center py-6">
        <main-progress-bar-component :upload-percentage="100/1.25"></main-progress-bar-component>
        <div class="font-bold text-xl md:text-2xl pb-6 pt-12 md:pt-20 leading-tight" v-html="title"></div>

        <form :action="route" id="frmClientLogin" method="post"
              @submit.prevent="submitForm"
        >
            <div class="text-center container md:max-w-100">
                <div class="wrap-form">
                    <div>
                        <div class="form-row !mb-0">
                            <input
                                type="file"
                                class="form-control"
                                @change="evt => handleFileUpload(evt)"
                                accept="image/*"
                                autocomplete="off">
                        </div>

                        <div class="py-6 text-white/75 text-center text-sm" v-html="LABEL_UPLOAD_PHOTO"></div>

                        <!-- Progress bar -->
                        <progress-bar-component class="mb-8"
                                                v-if="uploadPercentage > 0"
                                                :upload-percentage="uploadPercentage"
                        >
                        </progress-bar-component>

                        <div class="mx-auto w-40 mb-8">
                            <div v-if="fileName && fileName !== null && thumbnailSource && thumbnailSource !== null"
                                 class="my-4 flex flex-col justify-center">
                                <thumbnail-component-delete
                                    :full-path="thumbnailSource"
                                    :src="fileName"
                                    :img-class="'rounded-full overflow-hidden object-cover absolute z-10 w-full h-full'"
                                    :photo-class="'aspect-square rounded-full flex justify-center items-center bg-white photo border-2 border-bordeaux text-center relative mb-4'"
                                    :container-class="'!text-white'"
                                    @delete-photo="deleteFile(user.value?.id)"
                                >
                                </thumbnail-component-delete>
                            </div>
                            <no-uploaded-profile-photo-found-component v-else
                                                                       class="my-4"
                            :user="user"
                            ></no-uploaded-profile-photo-found-component>
                        </div>
                    </div>
                </div>
            </div>
            <div class="flex items-center justify-center gap-4 container max-w-100 md:w-100">
                <div class="flex-1" v-if="user.image !== null">
                    <ButtonComponent
                        title="Skip it"
                        :href="routeSuccess"
                        btn-class="btn-red-outline w-full text-xl !py-4"
                    ></ButtonComponent>
                </div>
                <div class="flex-1">
                    <SubmitButtonLargeComponent
                        title="Continue"
                        btn-class="btn-green-5 w-full !py-4"
                    >
                    </SubmitButtonLargeComponent>
                </div>
            </div>
        </form>
    </div>
</template>

<style scoped lang="scss">
.form {
    &-row {
        @apply mb-6;
    }
    &-control {
        @apply bg-white py-3;
    }
}
</style>
